/**
 * Checking a PST-Import file before it is imported: against the JSON Schema
 * (packages/api/assets/pst-import-v1.schema.json) and for references between
 * its parts. Files written in Claude Chat often broke on a name a few
 * characters too long, halfway through the import; this finds every such
 * spot first and says where, in words a person - or Claude - can act on.
 *
 * Only the subset of JSON Schema the PST schema uses: type, const, enum,
 * required, additionalProperties: false, properties, items, oneOf,
 * minLength, maxLength, pattern, minimum, maximum, minItems, maxItems.
 */
/** Keys PST writes into its exports for the reader and ignores when importing. */
export const IMPORT_NOTE_KEY = '_hinweise';
const typeOf = (v) => (v === null ? 'null' : Array.isArray(v) ? 'array' : typeof v === 'number' && Number.isInteger(v) ? 'integer' : typeof v);
const fits = (v, t) => { const a = typeOf(v); return a === t || (t === 'number' && a === 'integer'); };
const TYPE_WORDS = { string: 'Text', number: 'Zahl', integer: 'ganze Zahl', boolean: 'wahr/falsch', object: 'Objekt', array: 'Liste', null: 'null' };
/** Length as the server counts it (zod: UTF-16 code units). */
const chars = (s) => s.length;
/** A readable label for an entry of a list: its code and name, if it has any. */
function labelOf(v) {
    if (!v || typeof v !== 'object' || Array.isArray(v))
        return '';
    const o = v;
    const code = [o.code, o.oz, o.number].find((x) => typeof x === 'string' && x);
    const name = [o.name, o.shortText, o.description, o.label, o.title, o.company].find((x) => typeof x === 'string' && x);
    const short = name && chars(name) > 30 ? `${[...name].slice(0, 30).join('')} …` : name;
    return [code, short && `„${short}“`].filter(Boolean).join(' ');
}
function check(v, s, path, where, out) {
    const add = (message, extra = {}) => { out.push({ where: where || '(Datei)', path, message, ...extra }); };
    if (s.oneOf) {
        const trials = s.oneOf.map((alt) => { const o = []; check(v, alt, path, where, o); return o; });
        const ok = trials.find((t) => t.length === 0);
        if (!ok)
            out.push(...trials.reduce((a, b) => (b.length < a.length ? b : a)));
        return;
    }
    if (s.const !== undefined && v !== s.const)
        return add(`muss ${JSON.stringify(s.const)} sein`);
    if (s.enum && !s.enum.includes(v))
        return add(`erlaubt sind nur ${s.enum.map((e) => JSON.stringify(e)).join(', ')} – nicht ${JSON.stringify(v)}`);
    if (s.type) {
        const types = Array.isArray(s.type) ? s.type : [s.type];
        if (!types.some((t) => fits(v, t)))
            return add(`muss ${types.map((t) => TYPE_WORDS[t] ?? t).join(' oder ')} sein, ist ${TYPE_WORDS[typeOf(v)] ?? typeOf(v)}`);
    }
    if (typeof v === 'string') {
        const n = chars(v);
        // The server trims before it counts the minimum.
        if (s.minLength !== undefined && chars(v.trim()) < s.minLength)
            add(s.minLength === 1 ? 'darf nicht leer sein' : `mindestens ${s.minLength} Zeichen`);
        if (s.maxLength !== undefined && n > s.maxLength)
            add(`${n} Zeichen, erlaubt sind höchstens ${s.maxLength}`, { tooLong: { max: s.maxLength, actual: n } });
        if (s.pattern && !pattern(s.pattern).test(v))
            add(patternWords(s.pattern, v));
    }
    if (typeof v === 'number') {
        if (s.minimum !== undefined && v < s.minimum)
            add(`mindestens ${s.minimum}, ist ${v}`);
        if (s.maximum !== undefined && v > s.maximum)
            add(`höchstens ${s.maximum}, ist ${v}`);
    }
    if (Array.isArray(v)) {
        if (s.minItems !== undefined && v.length < s.minItems)
            add(`mindestens ${s.minItems} Einträge`);
        if (s.maxItems !== undefined && v.length > s.maxItems)
            add(`${v.length} Einträge, erlaubt sind höchstens ${s.maxItems}`);
        if (s.items)
            v.forEach((item, i) => { const l = labelOf(item); check(item, s.items, [...path, i], `${where}[${i}]${l ? ` (${l})` : ''}`, out); });
    }
    if (v && typeof v === 'object' && !Array.isArray(v) && s.properties) {
        const o = v;
        for (const k of s.required ?? [])
            if (o[k] === undefined)
                out.push({ where: `${where ? `${where}.` : ''}${k}`, path: [...path, k], message: 'fehlt (Pflichtangabe)' });
        for (const [k, val] of Object.entries(o)) {
            const sub = s.properties[k];
            const w = `${where ? `${where}.` : ''}${k}`;
            if (!sub) {
                if (s.additionalProperties === false && k !== IMPORT_NOTE_KEY)
                    out.push({ where: w, path: [...path, k], message: `unbekanntes Feld – erlaubt sind hier: ${Object.keys(s.properties).join(', ')}` });
                continue;
            }
            if (val === undefined)
                continue;
            check(val, sub, [...path, k], w, out);
        }
    }
}
const patterns = new Map();
const pattern = (p) => {
    let r = patterns.get(p);
    if (!r) {
        r = new RegExp(p, 'u');
        patterns.set(p, r);
    }
    return r;
};
function patternWords(pattern, v) {
    if (pattern === '^\\d{4}-\\d{2}-\\d{2}$')
        return `Datum als JJJJ-MM-TT, nicht „${v}“`;
    if (pattern.startsWith('^\\d{3}'))
        return `Kostengruppe als Zahl wie 330 oder 331.1, nicht „${v}“`;
    if (pattern.startsWith('^#[0-9a-fA-F]'))
        return `Farbe als #RRGGBB, nicht „${v}“`;
    if (pattern.startsWith('^[A-Za-z]'))
        return `nur Buchstaben, Ziffern und _, mit einem Buchstaben vorn (ohne Umlaute und Leerzeichen), nicht „${v}“`;
    return `passt nicht zum Muster ${pattern}`;
}
/** References inside the file: codes unique, predecessors and groups known. Tolerates any shape - the schema check reports that. */
function references(doc, out) {
    const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null);
    const list = (v) => (Array.isArray(v) ? v : []);
    const str = (v) => (typeof v === 'string' && v ? v : undefined);
    const s = obj(doc.schedule);
    if (!s)
        return;
    const groups = new Set(list(s.groups).map((g) => str(obj(g)?.code)));
    const tasks = list(s.tasks).map(obj);
    const milestones = list(s.milestones).map(obj);
    const seen = new Map();
    const note = (code, where) => {
        if (!code)
            return;
        if (seen.has(code))
            out.push({ where, path: [], message: `Nummer „${code}“ kommt doppelt vor (auch ${seen.get(code)}) – Nummern von Vorgängen und Meilensteinen müssen eindeutig sein` });
        else
            seen.set(code, where);
    };
    tasks.forEach((t, i) => note(str(t?.code), `schedule.tasks[${i}]`));
    milestones.forEach((m, i) => note(str(m?.code), `schedule.milestones[${i}]`));
    tasks.forEach((t, i) => {
        const group = str(t?.group);
        if (group && !groups.has(group))
            out.push({ where: `schedule.tasks[${i}].group`, path: ['schedule', 'tasks', i, 'group'], message: `Phase „${group}“ gibt es in schedule.groups nicht` });
        for (const p of list(t?.predecessors)) {
            const code = typeof p === 'string' ? p : str(obj(p)?.code);
            if (code && !seen.has(code))
                out.push({ where: `schedule.tasks[${i}].predecessors`, path: ['schedule', 'tasks', i, 'predecessors'], message: `Vorgänger „${code}“ gibt es nicht` });
        }
    });
    milestones.forEach((m, i) => {
        const group = str(m?.group);
        if (group && !groups.has(group))
            out.push({ where: `schedule.milestones[${i}].group`, path: ['schedule', 'milestones', i, 'group'], message: `Phase „${group}“ gibt es in schedule.groups nicht` });
        for (const code of list(m?.after))
            if (typeof code === 'string' && !seen.has(code))
                out.push({ where: `schedule.milestones[${i}].after`, path: ['schedule', 'milestones', i, 'after'], message: `Vorgänger „${code}“ gibt es nicht` });
    });
}
/** Every problem of a file, schema first, then references. */
export function checkImportDocument(doc, schema) {
    const out = [];
    check(doc, schema, [], '', out);
    if (doc && typeof doc === 'object')
        references(doc, out);
    return out;
}
/**
 * Texts over their limit, cut to it - the ending replaced by "…" so the cut
 * shows. Returns a copy; other problems stay for the person to mend.
 */
export function shortenTooLong(doc, issues) {
    const copy = structuredClone(doc);
    for (const i of issues) {
        if (!i.tooLong || i.path.length === 0)
            continue;
        let parent = copy;
        for (const k of i.path.slice(0, -1))
            parent = parent[k];
        const key = i.path[i.path.length - 1];
        const v = parent[key];
        if (typeof v !== 'string')
            continue;
        // Whole characters only (no half surrogate pair), so that with "…" it fits the server's count.
        let cut = '';
        for (const ch of v) {
            if (cut.length + ch.length > i.tooLong.max - 1)
                break;
            cut += ch;
        }
        parent[key] = `${cut.trimEnd()}…`;
    }
    return copy;
}
/** The problems as a short text to hand back to Claude Chat. */
export function issuesAsText(issues) {
    return [
        'PST hat die Import-Datei geprüft und diese Stellen gefunden. Bitte die Datei so korrigieren, dass alle Grenzen eingehalten sind, und vollständig neu ausgeben:',
        '',
        ...issues.map((i) => `- ${i.where}: ${i.message}`),
    ].join('\n');
}
/**
 * The limits of the schema as one line per field, for the notes an export
 * carries (`_hinweise.grenzen`), e.g. "schedule.tasks[].name": "Text, 1–300 Zeichen".
 */
export function schemaLimits(schema) {
    const out = {};
    const walk = (s, path, required) => {
        const node = s.oneOf ? s.oneOf.find((x) => x.type === 'object') ?? s.oneOf[0] : s;
        const parts = [];
        if (node.enum)
            parts.push(`einer von ${node.enum.map((e) => JSON.stringify(e)).join(', ')}`);
        if (node.maxLength !== undefined)
            parts.push(`${node.minLength ?? 0}–${node.maxLength} Zeichen`);
        else if (node.minLength)
            parts.push('nicht leer');
        if (node.pattern)
            parts.push(node.pattern === '^\\d{4}-\\d{2}-\\d{2}$' ? 'Datum JJJJ-MM-TT' : `Muster ${node.pattern}`);
        if (node.minimum !== undefined || node.maximum !== undefined)
            parts.push(`Zahl${node.minimum !== undefined ? ` ab ${node.minimum}` : ''}${node.maximum !== undefined ? ` bis ${node.maximum}` : ''}`);
        if (node.maxItems !== undefined)
            parts.push(`höchstens ${node.maxItems} Einträge`);
        if (path && (parts.length || required))
            out[path] = [required ? 'Pflicht' : '', ...parts].filter(Boolean).join(', ');
        for (const [k, sub] of Object.entries(node.properties ?? {}))
            walk(sub, path ? `${path}.${k}` : k, (node.required ?? []).includes(k));
        if (node.items)
            walk(node.items, `${path}[]`, false);
    };
    walk(schema, '', false);
    return out;
}
//# sourceMappingURL=import-check.js.map